import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, ApiError } from '../lib/api'
import { saveSession } from '../lib/auth'
import { useI18n, LOCALES, LOCALE_LABELS } from '../lib/i18n'
import Logo from '../components/Logo'
import { GoogleSignIn } from '../components/GoogleSignIn'
import { isDesktop } from '../lib/desktop'
import { startDesktopSignIn } from '../lib/desktopSignIn'
import { versionLabel } from '../lib/version'
import { TermsCheckbox } from '../components/TermsOfUse'
import { rememberReactivated } from '../components/AccountData'

/**
 * Maps the server's refusal to a sentence worth reading.
 *
 * `no_account` is the one that matters: it is not a failure so much as an instruction, and saying
 * "sign-in failed" there would leave somebody retrying a button that will never work for them.
 */
const GOOGLE_ERROR: Record<string, string> = {
  no_account: 'auth.googleNoAccount',
  no_organization: 'auth.googleNoAccount',
  email_unverified: 'auth.googleEmailUnverified',
  google_unavailable: 'auth.googleUnavailable',
  domain_not_allowed: 'auth.googleDomainNotAllowed',
  invalid_credentials: 'auth.invalidCredentials',
  terms_required: 'terms.required',
  // A password account connects Google from Conta, not from here (docs/refactor/54).
  google_not_linked: 'auth.googleNotLinked',
}

type Mode = 'signIn' | 'signUp' | 'forgot'

/** What another page left for this one to say: a password reset, a deactivation, a deletion scheduled. */
interface Handover {
  notice?: 'passwordReset' | 'deactivated' | 'deletionScheduled'
  date?: string
  forgot?: boolean
}

export default function SignIn() {
  const { t, locale, setLocale } = useI18n()
  const handover = (useLocation().state as Handover | null) ?? {}
  const [mode, setMode] = useState<Mode>(handover.forgot ? 'forgot' : 'signIn')
  // "Esqueci a senha" was sent: the same message whatever the address (docs/refactor/65).
  const [forgotSent, setForgotSent] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [organizationName, setOrganizationName] = useState('')
  // Creating an account needs the Terms of Use accepted: the button waits for the box.
  const [acceptTerms, setAcceptTerms] = useState(false)
  const termsMissing = mode === 'signUp' && !acceptTerms
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // A Google sign-in that found no account: kept, so "Criar organização" can finish it with just a
  // name and the terms instead of sending the person back to Google. The token lives about an hour.
  const [google, setGoogle] = useState<{ idToken: string; email: string } | null>(null)

  const switchMode = (next: Mode): void => {
    setMode(next)
    setError(null)
    setGoogle(null)
    setForgotSent(false)
  }

  // Served rather than built in, so the deployment can turn Google on without rebuilding the SPA.
  // A failure here is silent on purpose: no config means no button, and the password form is
  // untouched.
  const { data: config, isError: configFailed } = useQuery({
    queryKey: ['auth-config'],
    queryFn: api.auth.config,
    // The desktop app may open before the network is up: a few tries, then the button shows anyway (below).
    retry: isDesktop() ? 3 : false,
    staleTime: Infinity,
  })

  const signInWithGoogle = async (idToken: string): Promise<void> => {
    setError(null)
    setBusy(true)

    try {
      // On the sign-up tab the typed organization name comes along, which is what turns this from
      // "sign in" into "create an organization". On the sign-in tab it does not, and an unknown
      // Google account is refused rather than quietly given a tenant of its own.
      const result = await api.auth.google(
        idToken,
        mode === 'signUp' ? organizationName.trim() || undefined : undefined,
        mode === 'signUp' ? acceptTerms : undefined,
      )
      saveSession(result)
    } catch (caught) {
      if (caught instanceof ApiError && (caught.code === 'no_account' || caught.code === 'no_organization')) {
        // Not an error for somebody new: on to creating their organization, with Google already done.
        setGoogle({ idToken, email: googleEmail(idToken) })
        setMode('signUp')
        setDisplayName('')
      } else if (caught instanceof ApiError) {
        // A kept token that has expired: back to the Google button for a fresh one.
        if (caught.code === 'invalid_credentials') setGoogle(null)
        const key = GOOGLE_ERROR[caught.code ?? '']
        setError(key ? t(key) : caught.message)
      } else {
        setError(t('error.generic'))
      }
    } finally {
      setBusy(false)
    }
  }

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault()
    if (mode === 'signUp' && google) {
      await signInWithGoogle(google.idToken)
      return
    }
    setError(null)
    setBusy(true)

    try {
      if (mode === 'forgot') {
        await api.auth.forgotPassword(email)
        setForgotSent(true)
        return
      }
      const result =
        mode === 'signIn'
          ? await api.auth.signIn(email, password)
          : await api.auth.signUp(organizationName, email, password, displayName, acceptTerms)

      // "Desativar conta" undone by signing in: the app says welcome back, once.
      if (result.reactivated) rememberReactivated()
      saveSession(result)
    } catch (caught) {
      // The server's message is shown for domain failures — "password must be at least 12
      // characters" is more useful than a generic string. Anything else falls back to a
      // translated message, because a raw 500 tells the user nothing they can act on.
      if (caught instanceof ApiError && caught.status === 409) {
        setError(
          caught.code === 'invalid_credentials' ? t('auth.invalidCredentials')
            : caught.code === 'too_many_attempts' ? t('auth.tooManyAttempts')
            : caught.code === 'terms_required' ? t('terms.required')
            : caught.code === 'email_not_configured' ? t('auth.forgot.unavailable')
              : caught.message,
        )
      } else if (caught instanceof ApiError) {
        setError(t(caught.translationKey))
      } else {
        setError(t('error.generic'))
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="auth">
      <div className="auth__card">
        <div className="auth__brand">
          <Logo size={36} />
          <div>
            <div style={{ fontSize: 'var(--text-lg)', fontWeight: 800, letterSpacing: '-0.03em' }}>Vephus</div>
            <div className="auth__tagline">{t('brand.tagline')}</div>
          </div>
        </div>

        <div className="auth__tabs">
          <button
            type="button"
            className={`auth__tab${mode === 'signIn' ? ' auth__tab--active' : ''}`}
            onClick={() => switchMode('signIn')}
          >
            {t('auth.signIn')}
          </button>
          <button
            type="button"
            className={`auth__tab${mode === 'signUp' ? ' auth__tab--active' : ''}`}
            onClick={() => switchMode('signUp')}
          >
            {t('auth.signUp')}
          </button>
        </div>

        {handover.notice && (
          <p className="small auth-google-finish" role="status" style={{ marginBottom: 'var(--s3)' }}>
            {handover.notice === 'passwordReset' ? t('auth.reset.done')
              : handover.notice === 'deactivated' ? t('account.deactivate.done')
                : t('account.delete.scheduled', { date: handover.date ?? '' })}
          </p>
        )}

        {mode === 'forgot' && (forgotSent || config?.passwordRecovery === false) ? (
          <div className="stack">
            <p className="small" role="status" style={{ margin: 0 }}>
              {forgotSent ? t('auth.forgot.sent') : t('auth.forgot.unavailable')}
            </p>
            {!forgotSent && <Link className="btn btn--sm" to="/contact">{t('contact.title')}</Link>}
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => switchMode('signIn')}>{t('auth.forgot.back')}</button>
          </div>
        ) : (
        <form className="stack" onSubmit={submit}>
          {mode === 'forgot' && <p className="small muted" style={{ margin: 0 }}>{t('auth.forgot.body')}</p>}
          {mode === 'signUp' && google && (
            <p className="small auth-google-finish" role="status" style={{ margin: 0 }}>
              {t('auth.googleFinish', { email: google.email })}
            </p>
          )}
          {mode === 'signUp' && (
            <>
              <label className="field">
                <span className="field__label">{t('auth.organizationName')}</span>
                <input
                  className="input"
                  value={organizationName}
                  onChange={(e) => setOrganizationName(e.target.value)}
                  required
                  autoComplete="organization"
                  autoFocus={Boolean(google)}
                />
              </label>
            </>
          )}
          {mode === 'signUp' && !google && (
            <>
              <label className="field">
                <span className="field__label">{t('auth.displayName')}</span>
                <input
                  className="input"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  required
                  autoComplete="name"
                />
              </label>
            </>
          )}

          {!(mode === 'signUp' && google) && (<>
          <label className="field">
            <span className="field__label">{t('auth.email')}</span>
            <input
              className="input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
          </label>

          {mode !== 'forgot' && (
          <label className="field">
            <span className="field__label">{t('auth.password')}</span>
            <input
              className="input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete={mode === 'signIn' ? 'current-password' : 'new-password'}
              minLength={mode === 'signUp' ? 12 : undefined}
            />
            {mode === 'signUp' && <span className="field__hint">{t('auth.passwordHint')}</span>}
          </label>
          )}
          {mode === 'signIn' && (
            <button type="button" className="btn btn--ghost btn--sm auth__forgot" onClick={() => switchMode('forgot')} disabled={busy}>
              {t('auth.forgot.link')}
            </button>
          )}
          </>)}

          {mode === 'signUp' && <TermsCheckbox checked={acceptTerms} onChange={setAcceptTerms} disabled={busy} />}

          {error && (
            <div className="field__error" role="alert">
              {error}
            </div>
          )}

          <button type="submit" className="btn btn--primary full" disabled={busy || termsMissing}>
            {mode === 'forgot'
              ? busy ? t('auth.forgot.sending') : t('auth.forgot.submit')
              : busy
                ? mode === 'signIn' ? t('auth.signingIn') : t('auth.creating')
                : mode === 'signIn' ? t('auth.signIn') : google ? t('auth.googleCreate') : t('auth.signUp')}
          </button>
          {mode === 'forgot' && (
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => switchMode('signIn')} disabled={busy}>
              {t('auth.forgot.back')}
            </button>
          )}
          {mode === 'signUp' && google && (
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => switchMode('signUp')} disabled={busy}>
              {t('auth.googleCancel')}
            </button>
          )}
        </form>
        )}

        {/*
          Below the form, not above it. The password field is what most people came for, and a
          provider button placed first quietly reframes the page as being about Google.

          Renders nothing when the deployment has no client id, so an organization that has not
          configured Google sees exactly the page it saw before.
        */}
        {google || mode === 'forgot' ? null : isDesktop() ? (
          // Google does not accept the desktop window's origin: the app signs in through the browser. Shown also when
          // the settings could not be read (no network when the app opened): the website decides what it offers.
          config?.googleClientId || configFailed ? (
            <button type="button" className="btn auth-google-desktop" style={{ width: '100%', marginTop: 'var(--s3)', justifyContent: 'center', gap: 8 }} onClick={() => void startDesktopSignIn()} disabled={busy}>
              <GoogleMark /> {t('auth.googleInBrowser')}
            </button>
          ) : null
        ) : (
          <GoogleSignIn
            clientId={config?.googleClientId ?? null}
            onCredential={(idToken) => void signInWithGoogle(idToken)}
            // On the sign-up tab Google creates an account too, so it waits for the same box.
            disabled={busy || termsMissing}
          />
        )}

        {mode === 'signUp' && !google && config?.googleClientId && (
          <p className="tiny muted" style={{ marginTop: 'var(--s2)' }}>{t('auth.googleNeedsOrganization')}</p>
        )}

        <div className="row row--between" style={{ marginTop: 'var(--s5)' }}>
          <span className="tiny muted">{t('nav.language')}</span>
          <select
            className="select"
            style={{ width: 'auto' }}
            value={locale}
            onChange={(e) => setLocale(e.target.value as typeof locale)}
            aria-label={t('nav.language')}
          >
            {LOCALES.map((code) => (
              <option key={code} value={code}>
                {LOCALE_LABELS[code]}
              </option>
            ))}
          </select>
        </div>

        <p className="tiny muted signin__version">
          <a href="/terms">{t('terms.title')}</a> · <a href="/privacy">{t('privacy.title')}</a> · <a href="/contact">{t('contact.title')}</a> · {versionLabel()}
        </p>
      </div>
    </div>
  )
}

/**
 * The address a Google ID token names, to show the person which account they are finishing. Read
 * without checking the signature: display only — the server checks the token when it is used.
 */
function googleEmail(idToken: string): string {
  try {
    const payload = idToken.split('.')[1] ?? ''
    const json = decodeURIComponent(
      [...atob(payload.replace(/-/g, '+').replace(/_/g, '/'))].map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0')).join(''),
    )
    const email = (JSON.parse(json) as { email?: unknown }).email
    return typeof email === 'string' ? email : ''
  } catch {
    return ''
  }
}

/** Google's "G", for the desktop's button — drawn here, as the app window cannot load Google's. */
function GoogleMark() {
  return (
    <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden>
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
      <path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/>
    </svg>
  )
}
